/**
 * Ticket utility functions and helper components
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { BACKEND_BASE_URL } from '@/lib/apiBase';
import { sanitizeHtml, stripHtmlSafe } from '@/lib/sanitize';

/**
 * Rewrite inline attachment URLs in stored HTML to always use the current origin.
 *
 * Fixes the "broken image after deploy" issue where tickets created in the preview
 * environment had absolute URLs (https://preview-xyz.example.com/api/uploads/...)
 * baked into the HTML. When the app is served from a different domain (production
 * deployment, new preview URL, etc.) those absolute URLs 404. We rewrite anything
 * that looks like an uploads URL — regardless of host — to the relative form
 * `/api/uploads/{id}/download` and then prefix the current BACKEND_BASE_URL.
 *
 * Also rewrites bare `/api/uploads/...` paths to use the current origin explicitly
 * so they render correctly when the API is on a separate host.
 */
export function sanitizeAttachmentHtml(html) {
  if (!html || typeof html !== 'string') return html || '';
  const current = (BACKEND_BASE_URL || '').replace(/\/$/, '');
  // 1) Absolute URLs → strip host, keep /api/uploads/{id}/download path
  let out = html.replace(
    /(src|href)\s*=\s*"(https?:\/\/[^"]*?)(\/api\/uploads\/[^"]+?)"/gi,
    (_m, attr, _host, path) => `${attr}="${current}${path}"`
  );
  out = out.replace(
    /(src|href)\s*=\s*'(https?:\/\/[^']*?)(\/api\/uploads\/[^']+?)'/gi,
    (_m, attr, _host, path) => `${attr}='${current}${path}'`
  );
  // 2) Bare relative uploads URLs → prefix current backend origin
  out = out.replace(
    /(src|href)\s*=\s*"\/api\/uploads\/([^"]+?)"/gi,
    (_m, attr, tail) => `${attr}="${current}/api/uploads/${tail}"`
  );
  out = out.replace(
    /(src|href)\s*=\s*'\/api\/uploads\/([^']+?)'/gi,
    (_m, attr, tail) => `${attr}='${current}/api/uploads/${tail}'`
  );
  return out;
}

import {
  AlertCircle,
  CheckCircle,
  ChevronRight,
  Clock,
  RefreshCw,
  Reply,
  ReplyAll,
  Forward,
  Download,
  X,
  ZoomIn,
  ZoomOut,
  Search,
} from 'lucide-react';

// ==================== UTILITY FUNCTIONS ====================

export function stripHtml(html) {
  // DOMParser doesn't execute <script>/<img onerror=> like innerHTML does.
  return stripHtmlSafe(html);
}

export function formatDateCompact(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch (e) {
    return iso;
  }
}

export function formatDateTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  } catch (e) {
    return iso;
  }
}

export function formatRelativeTime(iso) {
  if (!iso) return '-';
  try {
    const d = new Date(iso).getTime();
    if (!d) return '-';
    const diff = Math.max(0, Date.now() - d);
    const sec = Math.floor(diff / 1000);
    if (sec < 60) return 'just now';
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const day = Math.floor(hr / 24);
    if (day < 30) return `${day}d ago`;
    const mo = Math.floor(day / 30);
    if (mo < 12) return `${mo}mo ago`;
    const yr = Math.floor(day / 365);
    return `${yr}y ago`;
  } catch (e) {
    return '-';
  }
}

// ==================== STATUS & PRIORITY BADGES ====================

export function StatusBadge({ status }) {
  const cfg = {
    open: { label: 'Open', cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: AlertCircle },
    in_progress: { label: 'In Progress', cls: 'bg-blue-50 text-blue-700 border-blue-200', icon: RefreshCw },
    waiting_on_customer: {
      label: 'Waiting',
      cls: 'bg-purple-50 text-purple-700 border-purple-200',
      icon: Clock,
    },
    under_investigation: {
      label: 'Investigating',
      cls: 'bg-cyan-50 text-cyan-700 border-cyan-200',
      icon: Search,
    },
    resolved: { label: 'Resolved', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
  };
  const c = cfg[status] || cfg.open;
  const Icon = c.icon;
  return (
    <Badge variant="outline" className={`${c.cls} border`}>
      <Icon className="w-3 h-3 mr-1" />
      {c.label}
    </Badge>
  );
}

export function PriorityBadge({ priority }) {
  const cfg = {
    low: { label: 'Low', cls: 'bg-slate-100 text-slate-700' },
    medium: { label: 'Medium', cls: 'bg-blue-100 text-blue-700' },
    high: { label: 'High', cls: 'bg-orange-100 text-orange-700' },
    critical: { label: 'Critical', cls: 'bg-rose-100 text-rose-700' },
  };
  const c = cfg[priority] || cfg.medium;
  return <Badge className={c.cls}>{c.label}</Badge>;
}

export function SlaBadge({ breached, dueAt, hours, paused }) {
  if (paused) {
    return (
      <Badge
        className="bg-slate-100 text-slate-700 border border-slate-200 text-[10px] font-semibold"
        title={dueAt ? `SLA paused — was due at ${new Date(dueAt).toLocaleString()}` : 'SLA paused'}
        data-testid="sla-paused-badge"
      >
        <AlertCircle className="w-3 h-3 mr-1" />
        SLA Paused
      </Badge>
    );
  }
  if (!hours) return null;
  if (breached) {
    return (
      <Badge
        className="bg-red-100 text-red-700 border border-red-200 text-[10px] font-semibold"
        title={dueAt ? `SLA due at ${new Date(dueAt).toLocaleString()}` : 'SLA breached'}
        data-testid="sla-breach-badge"
      >
        <AlertCircle className="w-3 h-3 mr-1" />
        SLA Breach
      </Badge>
    );
  }
  return null;
}

// ==================== IMAGE LIGHTBOX ====================

function ImageLightbox({ src, filename, onClose }) {
  const [zoomed, setZoomed] = useState(false);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85"
      onClick={onClose}
      data-testid="image-lightbox"
    >
      {/* Controls bar */}
      <div
        className="absolute top-4 right-4 flex items-center gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        <a
          href={src}
          download={filename}
          className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 text-white px-3 py-1.5 rounded-lg text-sm transition-colors"
          title="Download"
        >
          <Download className="w-4 h-4" />
          Download
        </a>
        <button
          onClick={() => setZoomed((z) => !z)}
          className="bg-white/10 hover:bg-white/20 text-white p-1.5 rounded-lg transition-colors"
          title={zoomed ? 'Zoom out' : 'Zoom in'}
        >
          {zoomed ? <ZoomOut className="w-4 h-4" /> : <ZoomIn className="w-4 h-4" />}
        </button>
        <button
          onClick={onClose}
          className="bg-white/10 hover:bg-white/20 text-white p-1.5 rounded-lg transition-colors"
          title="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Filename label */}
      <div className="absolute bottom-4 left-0 right-0 text-center text-white/70 text-sm px-4 truncate pointer-events-none">
        {filename}
      </div>

      {/* Image */}
      <div
        className={`${zoomed ? 'overflow-auto max-w-none max-h-none' : 'max-w-[90vw] max-h-[85vh]'} flex items-center justify-center`}
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={src}
          alt={filename}
          className={`rounded-lg shadow-2xl object-contain ${
            zoomed ? 'cursor-zoom-out w-auto h-auto max-w-none' : 'max-w-[90vw] max-h-[85vh] cursor-zoom-in'
          }`}
          onClick={() => setZoomed((z) => !z)}
        />
      </div>
    </div>
  );
}

// ==================== ATTACHMENT COMPONENTS ====================

export function AttachmentBlock({ attachments, messageHtml = '' }) {  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [lightboxFilename, setLightboxFilename] = useState('');
  const [failedImages, setFailedImages] = useState({});

  if (!attachments || attachments.length === 0) return null;

  // Hide image attachments whose URL is already embedded in the message HTML
  // (prevents "seeing 2 copies" of a pasted image).
  const filtered = attachments.filter((att) => {
    const mt = (att.mime_type || '').toLowerCase();
    if (!mt.startsWith('image/')) return true;
    if (!att.url || !messageHtml) return true;
    return !messageHtml.includes(att.url);
  });

  if (filtered.length === 0) return null;

  return (
    <>
      {lightboxSrc && (
        <ImageLightbox
          src={lightboxSrc}
          filename={lightboxFilename}
          onClose={() => setLightboxSrc(null)}
        />
      )}
      <div className="mt-3 space-y-2">
        <div className="text-xs font-medium text-slate-500">
          Attachments ({filtered.length})
        </div>
        <div className="grid gap-2 grid-cols-1 sm:grid-cols-2">
          {filtered.map((att) => {
            const url = `${BACKEND_BASE_URL}${att.url}`;
            const mt = (att.mime_type || '').toLowerCase();
            const isImage = mt.startsWith('image/');
            const isVideo = mt.startsWith('video/');
            const isPdf = mt === 'application/pdf';
            const imageFailed = failedImages[att.id];
            const showInlineThumb = isImage && !imageFailed;
            return (
              <div
                key={att.id}
                className="group flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-2 hover:border-indigo-200 hover:bg-slate-50/50 transition-colors"
              >
                {/* Thumbnail or icon */}
                {showInlineThumb ? (
                  <button
                    type="button"
                    onClick={() => { setLightboxSrc(url); setLightboxFilename(att.filename); }}
                    className="flex-shrink-0 w-12 h-12 rounded overflow-hidden bg-slate-100 border border-slate-200"
                    data-testid="attachment-image-thumbnail"
                    title="Click to preview"
                  >
                    <img
                      src={url}
                      alt={att.filename}
                      className="w-full h-full object-cover"
                      onError={() => setFailedImages((p) => ({ ...p, [att.id]: true }))}
                    />
                  </button>
                ) : (
                  <div className="flex-shrink-0 w-12 h-12 rounded bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-500">
                    {isVideo ? (
                      <span className="text-[10px] font-semibold">VIDEO</span>
                    ) : isPdf ? (
                      <span className="text-[10px] font-semibold">PDF</span>
                    ) : (
                      <span className="text-[10px] font-semibold">
                        {(att.filename || 'FILE').split('.').pop()?.slice(0, 4).toUpperCase() || 'FILE'}
                      </span>
                    )}
                  </div>
                )}

                {/* File info */}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-800 truncate" title={att.filename}>
                    {att.filename}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {mt || 'file'} · {att.size ? `${Math.round(att.size / 1024)} KB` : '—'}
                  </div>
                </div>

                {/* Download */}
                <a
                  href={url}
                  download={att.filename}
                  rel="noopener noreferrer"
                  className="flex-shrink-0 p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                  title="Download"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Download className="w-4 h-4" />
                </a>

                {/* Video inline below (small) */}
                {isVideo && (
                  <video controls className="hidden" preload="none">
                    <source src={url} type={att.mime_type} />
                  </video>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}

// ==================== CONVERSATION MESSAGE ====================

export function ConversationMessage({ align = 'left', title, subtitle, html, attachments, onReply, onReplyAll, onForward, replyType, defaultExpanded = true }) {
  const isRight = align === 'right';
  const [showActions, setShowActions] = useState(false);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const isPrivateNote = replyType === 'private_note';
  const isPublicNote = replyType === 'public_note';
  const isNote = isPrivateNote || isPublicNote;

  const containerClass = isPrivateNote
    ? 'bg-slate-100 border-slate-300 border-dashed'
    : isPublicNote
    ? 'bg-amber-50 border-amber-200'
    : isRight
    ? 'bg-indigo-50 border-indigo-200'
    : 'bg-white border-slate-200';

  const previewText = stripHtmlSafe(html).slice(0, 140);

  return (
    <div className={`flex ${isRight ? 'justify-end' : 'justify-start'} mb-2`}>
      <div
        className={`max-w-4xl w-full rounded-lg border ${containerClass} ${expanded ? 'p-4' : 'p-3'} relative transition-all`}
        onMouseEnter={() => setShowActions(true)}
        onMouseLeave={() => setShowActions(false)}
      >
        {/* Note pill */}
        {isNote && (
          <div className="absolute -top-2.5 left-3">
            <span
              className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${
                isPrivateNote
                  ? 'bg-slate-800 text-white border-slate-800'
                  : 'bg-amber-500 text-white border-amber-500'
              }`}
              data-testid={isPrivateNote ? 'private-note-label' : 'public-note-label'}
            >
              {isPrivateNote ? 'Private note' : 'Public note'}
            </span>
          </div>
        )}

        {/* Header — clickable to toggle expand/collapse */}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full text-left flex items-center gap-3 group"
          aria-expanded={expanded}
          data-testid="message-toggle"
        >
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0 ${
            isRight ? 'bg-indigo-200 text-indigo-700' : 'bg-slate-200 text-slate-700'
          }`}>
            {title?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2 flex-wrap">
              <div className="text-sm font-semibold text-slate-900 truncate">{title}</div>
              <div className="text-xs text-slate-500 flex-shrink-0">{subtitle}</div>
            </div>
            {!expanded && previewText && (
              <div className="text-xs text-slate-500 truncate mt-0.5" data-testid="message-preview">
                {previewText}
              </div>
            )}
          </div>
          <ChevronRight
            className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`}
          />
        </button>

        {expanded && (
          <>
            {/* Message Content */}
            <div
              className="crm-rich-content prose prose-sm prose-slate max-w-none text-slate-700 break-words overflow-x-auto mt-3 [&_img]:max-w-full [&_img]:h-auto [&_table]:max-w-full [&_table]:block [&_table]:overflow-x-auto [&_pre]:max-w-full [&_pre]:overflow-x-auto [&_iframe]:max-w-full [&_a]:break-all"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(sanitizeAttachmentHtml(html)) }}
            />

            {/* Attachments */}
            <AttachmentBlock attachments={attachments} messageHtml={html} />
          </>
        )}

        {/* Actions - shown on hover (only when expanded so collapsed cards stay compact) */}
        {expanded && showActions && (onReply || onReplyAll || onForward) && (
          <div className="absolute top-2 right-10 flex gap-1 bg-white/90 backdrop-blur rounded-md shadow-sm p-1 border border-slate-200">
            {onReply && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-slate-600 hover:text-indigo-600"
                onClick={onReply}
                data-testid="mode-reply"
              >
                <Reply className="w-3 h-3 mr-1" />
                Reply
              </Button>
            )}
            {onReplyAll && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-slate-600 hover:text-indigo-600"
                onClick={onReplyAll}
                data-testid="mode-reply-all"
              >
                <ReplyAll className="w-3 h-3 mr-1" />
                Reply All
              </Button>
            )}
            {onForward && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-slate-600 hover:text-indigo-600"
                onClick={onForward}
                data-testid="mode-forward"
              >
                <Forward className="w-3 h-3 mr-1" />
                Forward
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
